<script setup>
import arrow from '@/assets/images/footer/arrow.svg'
import SocialIcons from './SocialIcons.vue'
import { useDownloadDropOptions } from '../header/hooks'
import { useLocalePath, useSwitchLocalePath } from '#imports'
import { locales } from '@/utils/locale'
import { inApp } from '@/utils/inApp'
import { useComingSoonMessage } from '~/components/home/utils/message'
import { useFooterLinks } from './config'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const router = useRouter()
const showComingSoonMessage = useComingSoonMessage()
const year = new Date().getFullYear()

const dls = computed(() => useFooterLinks({ t, locale, localePath }))

// 处理 locales 数据，转换为 n-select 需要的格式
const languageOptions = computed(() => {
  return locales.map(item => ({
    label: item.title,
    value: item.code,
  }))
})
// 语言切换处理函数
function handleLocaleChange(code) {
  const switchLocalePath = useSwitchLocalePath()
  const path = switchLocalePath(code) || `/${code}`
  router.push(path)
}

const expandedItems = ref(new Set())

function toggleExpand(index) {
  if (expandedItems.value.has(index)) {
    expandedItems.value.delete(index)
  }
  else {
    expandedItems.value.add(index)
  }
}
</script>

<template>
  <div v-if="!inApp">
    <footer class="w-full pt-20 max-md:pt-5">
      <div class="w-[1200px] mx-auto flex space-x-10 rtl:space-x-reverse max-lg:w-full max-md:flex-wrap max-md:px-0 max-md:space-x-0">
        <div class="w-[315px] flex flex-col gap-6 max-md:w-full max-md:gap-4 max-md:items-start max-md:border-b max-md:border-border max-md:px-4">
          <NuxtLink :to="localePath('/')">
            <svgo-home-logo class="text-textPrimary w-[64px] h-[40px] max-md:w-[60px] max-md:h-[34px]" />
          </NuxtLink>

          <SocialIcons class="max-md:hidden" />

          <n-dropdown :options="useDownloadDropOptions()" class="rounded-xl bg-pageSecondary">
            <div>
              <button class="h-10 px-4 common rounded-[30px] inline-flex items-center justify-center max-md:hidden">
                <svgo-common-download class="w-[16px] h-[16px] me-2 text-textPrimary flex-shrink-0" />
                {{ $t('下载APP') }}
              </button>
            </div>
          </n-dropdown>
        </div>
        <div class="max-md:py-2 max-md:w-full" />
        <div
          v-for="i, index in dls"
          :key="index"
          class="min-w-[145px] flex flex-col flex-wrap flex-shrink gap-5 max-md:w-full max-md:gap-2  max-md:px-4"
        >
          <dt
            class="text-xl font-medium mb-2.5 max-md:cursor-pointer max-md:flex max-md:items-center max-md:justify-between max-md:text-sm max-md:mx-0"
            @click="toggleExpand(index)"
          >
            {{ i.label }}
            <span class="hidden max-md:block transition-transform" :class="{ 'rotate-180': expandedItems.has(index) }">

              <component
                :is="arrow"
                class="w-[12px] h-[12px] text-textPrimary"
              />
            </span>
          </dt>
          <template v-for="j, subIndex in i.list" :key="subIndex">
            <dd
              class="text-sm text-textSecondary transition-all max-md:hidden max-md:px-4"
              :class="{
                'max-md:!block max-md:my-1': expandedItems.has(index),
                'max-md:mb-5': expandedItems.has(index) && subIndex === i.list.length - 1,
              }"
            >
              <a
                :href="j.href || 'javascript:void(0)'"
                :target="j.href ? i.target : '_self'"
                :rel="i.rel"
                @click.stop="!j.href && showComingSoonMessage()"
              >{{ j.label }}</a>
            </dd>
          </template>
        </div>
      </div>

      <SocialIcons class-name="hidden flex-column max-md:flex max-md:px-4 max-md:gap-3 " />
    </footer>
    <div class="border-t border-border w-[1200px] mx-auto flex flex-col md:flex-row justify-between text-textSecondary py-8 mt-10 max-md:w-full max-md:text-xs max-md:items-center max-md:pb-[140px] max-md:px-4">
      <div class="flex items-center justify-center gap-2 max-md:w-full max-md:mb-4 max-md:flex-wrap">
        <span>Copyright &copy; {{ year }} TT All Rights Reserved</span>
      <!-- <span class="w-px h-[14px] mx-4 bg-textSecondary max-md:hidden" />
      <a href="#">{{ $t('用户协议') }}</a> -->
      </div>
      <n-select
        :value="locale"
        :options="languageOptions"
        size="small"
        class="!w-[180px] md:ms-4 max-md:w-full"
        @update:value="handleLocaleChange"
      />
    </div>
  </div>
</template>

<style scoped>
.social-icon-telegram :deep(path),
.social-icon-medium :deep(path),
.social-icon-linkedin :deep(path),
.social-icon-facebook :deep(path),
.social-icon-tiktok :deep(path) {
  fill: currentColor;
}

.social-icon-x :deep(circle),
.social-icon-facebook :deep(path:first-child),
.social-icon-telegram :deep(path:first-child) {
  fill: var(--btnSecondary);
}
</style>
